import axios from 'axios';
import { getCurrentLocale } from '../utils/locale';

// Create the API instance with the correct backend URL
// Empty string = same-origin (frontend served by backend on same domain)
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Tell the backend which language to answer in (errors, notifications).
    // Resolved server-side from user > team > org > browser; see utils/locale.
    const locale = getCurrentLocale();
    if (locale) {
      config.headers['Accept-Language'] = locale;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    // Requests may opt out of the auto-logout redirect (e.g. background
    // pollers) so a timer never navigates the user away mid-session.
    const skipAuthRedirect = error.config?.skipAuthRedirect;

    // 401 = missing credentials. A background poll fails quietly; a
    // user-initiated request clears the session and returns to login.
    if (status === 401) {
      if (skipAuthRedirect) {
        console.info('Notifications paused: your session has ended. Please log back in to resume updates.');
      } else {
        console.warn('Your session has ended or you are not signed in. Please log back in.');
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        localStorage.removeItem('userId');

        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    // 403 = signed in but not permitted (the backend also reports an
    // expired/invalid token as 403). Don't force a logout; log clearly.
    if (status === 403) {
      console.warn('This request was not authorized. If this keeps happening, please log back in.', error.response?.data);
    }

    return Promise.reject(error);
  }
);

/**
 * Extract error message from API error response
 * @param {Error} error - Axios error object
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error) => {
  if (error.response) {
    // Server responded with error
    return (
      error.response.data?.detail ||
      error.response.data?.message ||
      error.response.data?.error ||
      `Request failed with status ${error.response.status}`
    );
  } else if (error.request) {
    // Request made but no response
    return 'No response from server. Please check your connection.';
  } else {
    // Other errors
    return error.message || 'An unexpected error occurred';
  }
};

/**
 * Handle API response and extract data
 * @param {Promise} apiCall - Axios API call promise
 * @returns {Promise} - Resolves with data or rejects with formatted error
 */
export const handleApiCall = async (apiCall) => {
  try {
    const response = await apiCall;
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export default api;
